import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../../navbar.jsx';
import Questnavbar from './quest_navbar.jsx';
import Footer from '../../footer.jsx';
import { API_URL } from '../../../apiConfig';
import QuizComponent from './QuizComponent.jsx';
import TestModeQuizComponent from './TestModeQuizComponent.jsx';
import sanitizeHtml from 'sanitize-html';
import userStore from '../../../store/user.zustand';
import axios from 'axios';
import { toast } from 'react-toastify';

const AssessmentPublicPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [assessment, setAssessment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [remainingTries, setRemainingTries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startQuiz, setStartQuiz] = useState(false);
  const { isAuthenticated, setUser, setAuthenticated } = userStore();
  const quizRef = useRef(null);

  const checkAuthentication = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/users/me?_t=${Date.now()}`, {
        withCredentials: true,
        headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' },
      });

      if (response?.data?.success) {
        setUser(response.data.data);
        setAuthenticated(true);
        return true;
      } else {
        setAuthenticated(false);
        return false;
      }
    } catch (error) {
      setAuthenticated(false);
      return false;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const isAuth = await checkAuthentication();

        const assessmentResponse = await fetch(
          `${API_URL}/api/v1/admin/public/assessments/${slug}`
        );
        if (!assessmentResponse.ok) {
          throw new Error('Failed to fetch assessment');
        }
        const assessmentData = await assessmentResponse.json();
        setAssessment(assessmentData.assessment);

        const questionsResponse = await fetch(
          `${API_URL}/api/v1/admin/public/assessments/${slug}/questions`
        );
        if (!questionsResponse.ok) {
          throw new Error('Failed to fetch questions');
        }
        const questionsData = await questionsResponse.json();
        setQuestions(questionsData.questions);

        if (isAuth) {
          const attemptsResponse = await axios.get(
            `${API_URL}/api/v1/admin/public/assessments/${slug}/attempts`,
            { withCredentials: true }
          );
          if (attemptsResponse.data.success) {
            setRemainingTries(attemptsResponse.data.data.remainingTries);
          } else {
            throw new Error(attemptsResponse.data.message || 'Failed to fetch remaining attempts');
          }
        } else {
          setRemainingTries(null);
        }
      } catch (err) {
        setError(err.message || 'An error occurred');
        toast.error(err.message || 'An error occurred', { autoClose: 2500 });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, location.pathname]);

  const handleStartQuiz = async () => {
    const isAuth = await checkAuthentication();
    if (isAuth) {
      if (remainingTries <= 0) {
        toast.error('You have reached the maximum number of attempts', { autoClose: 2500 });
        return;
      }
      setStartQuiz(true);
    } else {
      toast.error('Please log in to start the quiz', { autoClose: 2500 });
      navigate('/login', { state: { from: location.pathname } });
    }
  };

  const handleBack = async () => {
    setStartQuiz(false);
    const isAuth = await checkAuthentication();
    if (isAuth) {
      try {
        const response = await axios.get(
          `${API_URL}/api/v1/admin/public/assessments/${slug}/attempts`,
          { withCredentials: true }
        );
        if (response.data.success) {
          setRemainingTries(response.data.data.remainingTries);
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch remaining attempts');
        toast.error(err.message || 'Failed to fetch remaining attempts', { autoClose: 2500 });
      }
    }
  };

  const sanitizeDescription = (html) => {
    const sanitized = sanitizeHtml(html, {
      allowedTags: ['p', 'b', 'i', 'u', 's', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'blockquote', 'table', 'tr', 'td', 'th', 'strong', 'em'],
      allowedAttributes: {
        a: ['href'],
        table: ['border'],
        td: ['colspan', 'rowspan'],
        th: ['colspan', 'rowspan'],
      },
    });
    return sanitized;
  };

  const renderHTML = (html) => {
    const sanitizedHTML = sanitizeDescription(html);
    return { __html: sanitizedHTML };
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    let durationString = '';
    if (hours > 0) {
      durationString += `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    if (remainingMinutes > 0) {
      durationString += `${hours > 0 ? ' ' : ''}${remainingMinutes} ${remainingMinutes === 1 ? 'minute' : 'minutes'}`;
    }
    return durationString || '0 minutes';
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-black text-xl animate-pulse">Loading...</div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !assessment) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-grow flex items-center justify-center">
          <div className="bg-red-100 text-red-700 p-6 rounded-xl shadow-home-hero">
            {error || 'Assessment not found or not published'}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (startQuiz) {
    return (
      <div className="flex flex-col min-h-screen">
        {assessment.assessmentMode === 1 ? (
          <>
            <Questnavbar onNavigate={(path) => quizRef.current?.navigate(path)} />
            <main className="flex-grow">
              <TestModeQuizComponent
                ref={quizRef}
                assessment={assessment}
                questions={questions}
              />
            </main>
            <Footer onNavigate={(path) => quizRef.current?.navigate(path)} />
          </>
        ) : (
          <>
            <Navbar />
            <main className="flex-grow">
              <QuizComponent
                assessment={assessment}
                questions={questions}
                onBack={handleBack}
              />
            </main>
            <Footer />
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <Navbar />
      <main className="flex-grow py-10 px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl p-6 max-w-4xl mx-auto shadow-home-hero">
          <h1 className="text-2xl sm:text-2xl font-extrabold mb-6 text-gray-800">
            {assessment.title}
          </h1>
          <div className="mb-6 flex justify-between">
            <div>
              <p className="text-lg text-gray-700">
                <span className="font-semibold">Duration:</span>{' '}
                {formatDuration(assessment.duration)}
              </p>
              <p className="text-lg text-gray-700">
                <span className="font-semibold">Total Questions:</span> {questions.length}
              </p>
            </div>
            <div>
              <p className="text-lg text-gray-700">
                <span className="font-semibold">Maximum Attempts:</span> {assessment.maxTries}
              </p>
              {isAuthenticated && (
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Remaining Attempts:</span>{' '}
                  {remainingTries !== null ? remainingTries : 'Loading...'}
                </p>
              )}
            </div>
          </div>
          {assessment.description && (
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Description</h2>
              <div
                className="text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={renderHTML(assessment.description)}
              />
            </div>
          )}
          {assessment.instructions && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Instructions</h2>
              <div
                className="text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={renderHTML(assessment.instructions)}
              />
            </div>
          )}
          {isAuthenticated && remainingTries === 0 && (
            <div className="mb-6 text-red-700 bg-red-100 p-4 rounded-xl">
              You have reached the maximum number of attempts for this assessment.
            </div>
          )}
          <div className="flex justify-center">
            <button
              onClick={handleStartQuiz}
              disabled={isAuthenticated && remainingTries === 0}
              className={`px-8 py-3 rounded-full text-lg font-medium shadow-md transition duration-300 ease-in-out ${
                isAuthenticated && remainingTries === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
              aria-label="Start the quiz"
            >
              {isAuthenticated ? 'Start Quiz' : 'Log in to Start Quiz'}
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AssessmentPublicPage;